// src/pages/LoginPage.js — with 2FA support + forgot password link
import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import toast from 'react-hot-toast';

export default function LoginPage() {
  const [step, setStep] = useState('credentials');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState(['','','','','','']);
  const [tempToken, setTempToken] = useState('');
  const [sentTo, setSentTo] = useState('');
  const [deliveryMethod, setDeliveryMethod] = useState('email');
  const [trustDevice, setTrustDevice] = useState(true);
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const { loadUser } = useAuth();
  const navigate = useNavigate();
  const codeRefs = [useRef(), useRef(), useRef(), useRef(), useRef(), useRef()];

  const getDeviceToken  = () => localStorage.getItem('njjba_device_token');
  const saveDeviceToken = (token) => { if (token) localStorage.setItem('njjba_device_token', token); };

  const handleCredentials = async (e) => {
    e.preventDefault();
    if (!email || !password) return;
    setLoading(true);
    try {
      const res = await api.post('/auth/staff/login', {
        email, password, deviceToken: getDeviceToken(),
      });
      if (res.data.requiresTwoFactor) {
        setTempToken(res.data.tempToken);
        setSentTo(res.data.sentTo);
        setDeliveryMethod(res.data.deliveryMethod);
        setStep('verify_2fa');
        toast.success(res.data.message);
      } else {
        localStorage.setItem('njjba_access_token', res.data.accessToken);
        if (res.data.deviceToken) saveDeviceToken(res.data.deviceToken);
        await loadUser();
        navigate('/');
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Login failed.');
    } finally {
      setLoading(false);
    }
  };

  const handleCodeInput = (index, value) => {
    if (value.length === 6 && /^\d{6}$/.test(value)) {
      const digits = value.split('');
      setCode(digits);
      codeRefs[5]?.current?.focus();
      return;
    }
    if (!/^\d?$/.test(value)) return;
    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);
    if (value && index < 5) codeRefs[index + 1]?.current?.focus();
  };

  const handleCodeKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      codeRefs[index - 1]?.current?.focus();
    }
  };

  const handleVerify = async (submitCode) => {
    const fullCode = submitCode || code.join('');
    if (fullCode.length !== 6) return;
    setLoading(true);
    try {
      const res = await api.post('/auth/staff/verify-2fa', {
        tempToken, code: fullCode,
        trustThisDevice: trustDevice,
        deviceName: getDeviceName(),
      });
      localStorage.setItem('njjba_access_token', res.data.accessToken);
      if (res.data.deviceToken) saveDeviceToken(res.data.deviceToken);
      await loadUser();
      toast.success('Welcome back!');
      navigate('/');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Invalid code.');
      setCode(['','','','','','']);
      codeRefs[0]?.current?.focus();
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setResending(true);
    try {
      const res = await api.post('/auth/staff/resend-2fa', { tempToken });
      setSentTo(res.data.sentTo);
      toast.success(`New code sent to ${res.data.sentTo}`);
      setCode(['','','','','','']);
      codeRefs[0]?.current?.focus();
    } catch {
      toast.error('Failed to resend code.');
    } finally {
      setResending(false);
    }
  };

  useEffect(() => {
    if (step === 'verify_2fa' && code.join('').length === 6) {
      handleVerify(code.join(''));
    }
  }, [code]);

  function getDeviceName() {
    const ua = navigator.userAgent;
    if (/iPhone/.test(ua)) return 'iPhone';
    if (/iPad/.test(ua)) return 'iPad';
    if (/Android/.test(ua)) return 'Android';
    if (/Mac/.test(ua)) return 'Mac';
    if (/Windows/.test(ua)) return 'Windows PC';
    return 'Browser';
  }

  return (
    <div style={{
      minHeight: '100vh', background: 'var(--navy)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
    }}>
      <div style={{ width: '100%', maxWidth: 400 }}>
        <div style={{ textAlign: 'center', marginBottom: 40 }}>
          <h1 style={{ fontFamily: 'var(--font-serif)', fontSize: '2rem', color: 'white', marginBottom: 6 }}>NJJBA</h1>
          <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.875rem', letterSpacing: '1px', textTransform: 'uppercase' }}>
            Chamber Management
          </p>
        </div>

        {step === 'credentials' && (
          <div className="card" style={{ padding: '32px 36px' }}>
            <h2 style={{ marginBottom: 24, color: 'var(--navy)', fontFamily: 'var(--font-serif)' }}>Staff Sign In</h2>
            <form onSubmit={handleCredentials}>
              <div className="form-group">
                <label className="form-label">Email Address</label>
                <input type="email" className="form-input" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@njjba.com" autoFocus required />
              </div>
              <div className="form-group">
                <label className="form-label">Password</label>
                <input type="password" className="form-input" value={password} onChange={e => setPassword(e.target.value)} placeholder="••••••••" required />
              </div>
              <button type="submit" className="btn btn-primary w-full btn-lg" disabled={loading} style={{ marginTop: 8, justifyContent: 'center' }}>
                {loading ? <><span className="spinner" style={{ width: 16, height: 16 }} /> Signing in...</> : 'Sign In'}
              </button>
              <div style={{ textAlign: 'center', marginTop: 16 }}>
                <Link to="/forgot-password" style={{ fontSize: '0.85rem', color: 'var(--navy)', textDecoration: 'none' }}>
                  Forgot password?
                </Link>
              </div>
            </form>
          </div>
        )}

        {step === 'verify_2fa' && (
          <div className="card" style={{ padding: '32px 36px' }}>
            <div style={{ textAlign: 'center', marginBottom: 24 }}>
              <div style={{ fontSize: '2.5rem', marginBottom: 12 }}>{deliveryMethod === 'sms' ? '📱' : '📧'}</div>
              <h2 style={{ color: 'var(--navy)', fontFamily: 'var(--font-serif)', marginBottom: 8 }}>Verify Your Identity</h2>
              <p style={{ fontSize: '0.875rem', color: 'var(--gray-500)' }}>
                We sent a 6-digit code to<br />
                <strong style={{ color: 'var(--gray-700)' }}>{sentTo}</strong>
              </p>
            </div>

            <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginBottom: 20 }}>
              {code.map((digit, i) => (
                <input
                  key={i}
                  ref={codeRefs[i]}
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  value={digit}
                  onChange={e => handleCodeInput(i, e.target.value)}
                  onKeyDown={e => handleCodeKeyDown(i, e)}
                  autoFocus={i === 0}
                  style={{
                    width: 44, height: 52, textAlign: 'center',
                    fontSize: '1.5rem', fontWeight: 700, fontFamily: 'monospace',
                    border: `2px solid ${digit ? 'var(--navy)' : 'var(--gray-300)'}`,
                    borderRadius: 'var(--radius)', outline: 'none', color: 'var(--navy)',
                    transition: 'border-color 0.15s',
                  }}
                />
              ))}
            </div>

            <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 20, fontSize: '0.875rem', cursor: 'pointer' }}>
              <input type="checkbox" checked={trustDevice} onChange={e => setTrustDevice(e.target.checked)} />
              <span style={{ color: 'var(--gray-600)' }}>Trust this device for 90 days</span>
            </label>

            <button
              className="btn btn-primary w-full btn-lg"
              disabled={loading || code.join('').length !== 6}
              onClick={() => handleVerify()}
              style={{ justifyContent: 'center', marginBottom: 12 }}
            >
              {loading ? <><span className="spinner" style={{ width: 16, height: 16 }} /> Verifying...</> : 'Verify'}
            </button>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <button className="btn btn-ghost btn-sm" onClick={() => { setStep('credentials'); setCode(['','','','','','']); }} style={{ color: 'var(--gray-400)', fontSize: '0.8rem' }}>
                ← Back
              </button>
              <button className="btn btn-ghost btn-sm" onClick={handleResend} disabled={resending} style={{ fontSize: '0.8rem', color: 'var(--navy)' }}>
                {resending ? 'Sending...' : 'Resend code'}
              </button>
            </div>
          </div>
        )}

        <p style={{ textAlign: 'center', color: 'rgba(255,255,255,0.3)', fontSize: '0.75rem', marginTop: 24 }}>
          New Jersey Jewish Business Alliance · portal.njjba.com
        </p>
      </div>
    </div>
  );
}
